import {AxiosResponse} from 'axios';
import {PixagriField} from 'library/src/fields/models/PixagriField';
import {FieldIrrigationService} from 'library/src/fields/models/WagoField';
import {DateTime, Interval} from 'luxon';
import FieldsState from 'modules/fields/models/FieldsState';
import {reloadFieldsSummaryAfterDelay} from 'modules/fields/sagas/FieldsSagas';
import {CallEffect, all, call, fork, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {IrrigationDataPicked} from 'shared/configs/AppEnums';
import {AlertCode} from 'shared/models/AlertModel';
import log from 'shared/services/LogService';
import {AppState} from 'shared/store';
import {AutoId} from '../../../library/src/core/services/AutoId';
import {addAlert, fetchError, fetchStart, fetchSuccess, removeAlert} from '../../../shared/actions/Common';
import api, {pixagriApiUrl} from '../../../shared/services/ApiConfig';
import actions, {
  ADD_IRRIGATION,
  ADD_RAIN,
  AddIrrigationAction,
  AddRainAction,
  COMPARE_FIELDS,
  CompareFieldsAction,
  DELETE_IRRIGATION,
  DELETE_RAIN,
  DeleteIrrigationAction,
  DeleteRainAction,
  GET_PDF_WATERBALANCE,
  LOAD_WATER_BALANCE,
  LoadWaterBalanceAction,
  MODIFY_IRRIGATION,
  MODIFY_PHENO_STAGE_DATE,
  MODIFY_RAIN,
  MODIFY_WATER_INTAKE,
  ModifyIrrigationAction,
  ModifyPhenoStageDateAction,
  ModifyRainAction,
  ModifyWaterIntakeAction,
} from '../actions/WaterBalanceActions';
import {getWaterBalanceRoutePath} from '../configs/WaterBalanceRoutePath';
import DeleteDataModel from '../models/DeleteDataModel';
import IrrigationDataModel, {IrrigationDataReviewModel} from '../models/IrrigationDataModel';
import ModifyDataModel from '../models/ModifyDataModel';
import NewDataModel from '../models/NewDataModel';
import NewIrrigationModel from '../models/NewIrrigationModel';
import NewRainModel from '../models/NewRainModel';
import {VegetationCompareFieldDataModel} from '../models/VegetationCompareFieldDataModel';
import VegetationDataModel from '../models/VegetationDataModel';
import WaterBalanceModel, {WaterBalanceWithAssets} from '../models/WaterBalanceModel';
import {WaterBalancePhenoStage} from '../models/WaterBalancePhenoStage';
import WaterBalanceSortedModel from '../models/WaterBalanceSortedModel';
import WaterBalanceState from '../models/WaterBalanceState';

function getWaterBalanceRequest(fieldId: number, rotationId: number, forceCompute?: boolean) {
  // const waterBalanceUrl = `${pixagriApiUrl}/fields/${fieldId}/rotations/${rotationId}/water_balance`;
  const waterBalanceUrl = pixagriApiUrl + getWaterBalanceRoutePath(fieldId, rotationId, forceCompute);
  if (forceCompute) {
    return api.post(waterBalanceUrl);
  } else {
    return api.get(waterBalanceUrl);
  }
}

function sortWaterBalanceResult(rawResult: WaterBalanceModel[]) {
  const waterBalance = {} as WaterBalanceSortedModel;

  rawResult.map((x) => {
    if (!waterBalance.hasOwnProperty(x.wb_type)) {
      waterBalance[x.wb_type] = [];
      waterBalance[x.wb_type].push(x as WaterBalanceModel);
      return waterBalance[x.wb_type];
    }
    return waterBalance[x.wb_type].push(x as WaterBalanceModel);
  });
  return waterBalance;
}

function fillCompareFieldDataChart(
  waterBalance: WaterBalanceSortedModel,
  rotation: FieldIrrigationService,
  baseYear: number,
) {
  const fieldDataChartArray: VegetationCompareFieldDataModel[] = [];
  const rotationStartDateYear = DateTime.fromISO(rotation.campaign_start).get('year');
  const yearDiff = baseYear - rotationStartDateYear;

  waterBalance.fcover_verde?.forEach((data) => {
    let dataChart: VegetationCompareFieldDataModel = {};

    dataChart.fcover_verde = data.wb_value;
    dataChart.date = data.wb_date;
    dataChart.original_date_epoch = DateTime.fromISO(data.wb_date).toMillis();
    if (dataChart.original_date_epoch) {
      const customDate = DateTime.fromMillis(dataChart.original_date_epoch)
        .plus({
          year: yearDiff,
        })
        .toMillis();
      dataChart.custom_date_epoch = customDate;
    }

    if (waterBalance.GDD) {
      const gddValue = waterBalance.GDD.find((gdd) => gdd.wb_date === data.wb_date)?.wb_value;
      if (gddValue) {
        dataChart.gdd = parseFloat(gddValue);
      }
    } else {
      log.error(`No GDD data for field ${rotation.field_id}`);
    }

    const sowingDate = rotation.sowing_date;
    const intervall = Interval.fromDateTimes(DateTime.fromISO(sowingDate), DateTime.fromISO(data.wb_date));
    dataChart.day_number = intervall.length('day');

    fieldDataChartArray.push(dataChart);
  });
  if (waterBalance.lai_verde) {
    waterBalance.lai_verde.forEach((data) => {
      const existingPoint = fieldDataChartArray.find((x) => x.date === data.wb_date);
      if (existingPoint) {
        existingPoint.lai_verde = data.wb_value;
      } else {
        let dataChart: VegetationCompareFieldDataModel = {};

        dataChart.fcover_verde = data.wb_value;
        dataChart.lai_verde = data.wb_value;
        dataChart.date = data.wb_date;
        dataChart.original_date_epoch = DateTime.fromISO(data.wb_date).toMillis();
        if (dataChart.original_date_epoch) {
          const customDate = DateTime.fromMillis(dataChart.original_date_epoch)
            .plus({
              year: yearDiff,
            })
            .toMillis();
          dataChart.custom_date_epoch = customDate;
        }

        if (waterBalance.GDD) {
          const gddValue = waterBalance.GDD.find((gdd) => gdd.wb_date === data.wb_date)?.wb_value;
          if (gddValue) {
            dataChart.gdd = parseFloat(gddValue);
          }
        } else {
          log.error(`No GDD data for field ${rotation.field_id}`);
        }

        const sowingDate = rotation.sowing_date;
        const intervall = Interval.fromDateTimes(DateTime.fromISO(sowingDate), DateTime.fromISO(data.wb_date));
        dataChart.day_number = intervall.length('day');

        fieldDataChartArray.push(dataChart);
      }
    });
  }
  if (waterBalance.ndvi_planet) {
    waterBalance.ndvi_planet.forEach((data) => {
      const existingPoint = fieldDataChartArray.find((x) => x.date === data.wb_date);
      if (existingPoint) {
        existingPoint.ndvi_planet = data.wb_value;
      } else {
        let dataChart: VegetationCompareFieldDataModel = {};

        dataChart.fcover_verde = data.wb_value;
        dataChart.ndvi_planet = data.wb_value;
        dataChart.date = data.wb_date;
        dataChart.original_date_epoch = DateTime.fromISO(data.wb_date).toMillis();
        if (dataChart.original_date_epoch) {
          const customDate = DateTime.fromMillis(dataChart.original_date_epoch)
            .plus({
              year: yearDiff,
            })
            .toMillis();
          dataChart.custom_date_epoch = customDate;
        }

        if (waterBalance.GDD) {
          const gddValue = waterBalance.GDD.find((gdd) => gdd.wb_date === data.wb_date)?.wb_value;
          if (gddValue) {
            dataChart.gdd = parseFloat(gddValue);
          }
        } else {
          log.error(`No GDD data for field ${rotation.field_id}`);
        }

        const sowingDate = rotation.sowing_date;
        const intervall = Interval.fromDateTimes(DateTime.fromISO(sowingDate), DateTime.fromISO(data.wb_date));
        dataChart.day_number = intervall.length('day');

        fieldDataChartArray.push(dataChart);
      }
    });
  }

  return fieldDataChartArray.sort((a, b) => {
    return a.original_date_epoch! - b.original_date_epoch!;
  });
}
function* loadFieldsWaterBalance(action: CompareFieldsAction) {
  try {
    yield put(fetchStart('loadFieldsWaterBalance'));

    const {fieldsList, selectedFieldId, selectedCampaign}: FieldsState = yield select(
      (state: AppState) => state.fields,
    );

    if (!fieldsList || !selectedFieldId || !selectedCampaign) {
      throw new Error('no data');
    }

    const rotations = action.payload;
    const callWaterBalanceRequests: CallEffect<any>[] = [];
    const fieldsDataChart: {[key: string]: VegetationCompareFieldDataModel[]} = {};
    /**
     * get data for selectedRotation and set a base year reference to compute custom date
     */
    let currentRotation: FieldIrrigationService | null = null;
    let currentYear: number = 2021;
    if (fieldsList && selectedFieldId && selectedCampaign) {
      const selectedField: PixagriField | null =
        Object.values(fieldsList).find((field) => field.field_id === selectedFieldId) ?? null;
      if (selectedField && selectedField.irrigation && selectedField.irrigation.length) {
        const rotation = selectedField.irrigation.find((rot) => rot.campaign_id === selectedCampaign);
        if (rotation) {
          currentRotation = rotation;
          currentYear = DateTime.fromISO(rotation.campaign_start).get('year');
        }
      }
    }
    if (currentRotation) {
      const res: AxiosResponse = yield call(getWaterBalanceRequest, selectedFieldId, currentRotation.rotation_id);
      const dataSorted = sortWaterBalanceResult(res.data as WaterBalanceModel[]);

      fieldsDataChart[currentRotation.rotation_id] = fillCompareFieldDataChart(
        dataSorted,
        currentRotation,
        currentYear,
      );
    }
    /**
     * Retrieve fields from state and selected rotations
     * stack requests to send
     * if error, log.error and return in order to not throw entire saga, just skip field
     */
    rotations.forEach((rot) => {
      callWaterBalanceRequests.push(call(getWaterBalanceRequest, rot.field_id, rot.rotation_id));
    });

    log.debug(`launching ${callWaterBalanceRequests.length} waterBalance request`);
    // send requests
    const responses: AxiosResponse<any>[] = yield all(callWaterBalanceRequests);

    responses.forEach((res, index) => {
      // log and skip is response send error status
      if (res.status >= 400) {
        log.error(`Request for field ${rotations[index].field_id} res with status ${res.status} : ${res.statusText}`);
        return;
      }

      const rawResult = res.data as WaterBalanceModel[];
      const waterBalance: WaterBalanceSortedModel = sortWaterBalanceResult(rawResult);

      const rotation = rotations[index];
      fieldsDataChart[rotation.rotation_id] = fillCompareFieldDataChart(waterBalance, rotation, currentYear);
    });
    yield put(actions.compareFieldsSuccess(fieldsDataChart));
    yield put(fetchSuccess('loadFieldsWaterBalance'));
  } catch (error: any) {
    log.error(error);
    yield put(fetchError('loadFieldsWaterBalance', error.message));
  }
}

function* computeWaterBalanceAndReloadFieldsSummary(fieldId: number, rotationId: number) {
  try {
    yield call(getWaterBalanceRequest, fieldId, rotationId, true);
    yield call(reloadFieldsSummaryAfterDelay, 0, fieldId ? [fieldId] : [], true);
  } catch (error: any) {
    log.error(error);
  }
}
function* loadWaterBalance(data: LoadWaterBalanceAction) {
  try {
    const fieldId = data.payload.fieldId;
    const rotationId = data.payload.rotationId;
    const forceCompute = data.payload.forceCompute;
    //yield put(fetchStart('loadWaterBalance'));
    const alertId = AutoId.newId('waterbalance');
    yield put(
      addAlert({
        id: alertId,
        type: 'info',
        message: 'Chargement en cours...',
        code: AlertCode.LOAD_WATER_BALANCE,
        unClearOnClose: true,
        fieldId: fieldId,
      }),
    );
    const {summaryIrrigationInfo}: WaterBalanceState = yield select((state: AppState) => state.waterBalance);

    const {fieldsList: fieldsData, selectedCampaign}: FieldsState = yield select((state: AppState) => state.fields);

    if (!fieldsData || !selectedCampaign || !fieldId || !rotationId) {
      throw new Error('no fields data');
    }

    const res: AxiosResponse<any> = yield call(getWaterBalanceRequest, fieldId, rotationId, forceCompute);
    const rawResult = res.data as WaterBalanceModel[];
    const waterBalance = {} as WaterBalanceSortedModel;
    if (rawResult) {
      rawResult.map((x) => {
        if (x.hasOwnProperty('wb_advice') && x.wb_advice === 0) {
          return null;
        }
        if (!waterBalance.hasOwnProperty(x.wb_type)) {
          waterBalance[x.wb_type] = [];
          waterBalance[x.wb_type].push(x as WaterBalanceModel);
          return waterBalance[x.wb_type];
        }
        return waterBalance[x.wb_type].push(x as WaterBalanceModel);
      });
    }
    const renameKeys = (data: WaterBalanceModel[]) => {
      return data.map((item) => {
        const renamedItem: WaterBalanceWithAssets = {
          ...item,
          img_url: item.fcover_verde_pic ? item.fcover_verde_pic : '',
          bbox: item.fcover_verde_pic_bbox ? item.fcover_verde_pic_bbox : '',
        };
        delete renamedItem.fcover_verde_pic;
        delete renamedItem.fcover_verde_pic_bbox;
        return renamedItem;
      });
    };

    if (waterBalance.fcover_verde) {
      waterBalance.fcover_verde = renameKeys(waterBalance.fcover_verde);
    }

    if (waterBalance.lai_verde) {
      waterBalance.lai_verde = waterBalance.lai_verde.map((item) => {
        const addEpoch = {
          ...item,
          wb_dateEpoch: Date.parse(item.wb_date),
        };
        return addEpoch;
      });
    }

    if (waterBalance.ndvi_planet) {
      waterBalance.ndvi_planet = waterBalance.ndvi_planet.map((item) => {
        const addEpoch = {
          ...item,
          wb_dateEpoch: Date.parse(item.wb_date),
        };
        return addEpoch;
      });
    }
    /*

    !Debug code to handle phenology, currently not render by wb request (10/05/2023)

    if (waterBalance.GDD.length) {
      phenoStages58.forEach((phenoStage) => {
        const value = phenoStage.stage_value;
        const closestGdd = waterBalance.GDD.sort((a, b) => {
          return (
            Math.abs(value - parseFloat(a.wb_value)) -
            Math.abs(value - parseFloat(b.wb_value))
          );
        })[0];

        const gddStageWaterbalance: GddStageWaterBalance = {
          wb_type: 'GDD_stage',
          wb_value: value.toString(),
          wb_date: closestGdd.wb_date,
          crop_id: 58,
          stage_type: phenoStage.stage_type,
        };
        if (
          !waterBalance.GDD_stage ||
          (waterBalance.GDD_stage &&
            !waterBalance.GDD_stage.find(
              (stage) => stage.wb_date === gddStageWaterbalance.wb_date,
            ))
        ) {
          if (!waterBalance.GDD_stage) {
            waterBalance.GDD_stage = [];
          }
          waterBalance.GDD_stage.push(gddStageWaterbalance);
        }
      });
    }

    console.log(waterBalance);*/

    //create array for vegetation chart's data
    const vegetationDataChart = [] as VegetationDataModel[];

    //create array for irrigation datas' charts
    const irrigationDataChart = [] as IrrigationDataModel[];

    //create array for pheno stages
    const irrigationDataReviewChart = [] as IrrigationDataReviewModel[];

    // function to fill the vegetation data chart

    function fillArrayChartVegetation(key: string, dataArray: VegetationDataModel[]) {
      if (waterBalance[key] === undefined) {
        log.debug(`waterBalance key ${key} is undefined`);
        return;
      }
      waterBalance[key].filter((value) => {
        const index = dataArray.findIndex((x) => x.wb_date === value.wb_date);
        if (index === -1) {
          dataArray.push({
            wb_date: value.wb_date,
            wb_dateEpoch: DateTime.fromISO(value.wb_date).toMillis(),
            [`wb_value_${key}`]: parseFloat(value.wb_value),
          } as VegetationDataModel);
        } else {
          dataArray[index][`wb_value_${key}`] = parseFloat(value.wb_value);
        }
        return dataArray;
      });
    }

    let maxIrrigationChartValue = 0;
    // function to fill the irrigation data chart array
    function fillArrayChartIrrigation(key: string, dataArray: IrrigationDataModel[]) {
      if (waterBalance[key] === undefined) {
        log.debug(`waterBalance key ${key} is undefined`);
        return;
      }
      waterBalance[key].filter((value) => {
        const index = dataArray.findIndex((x) => x.wb_dateEpoch === DateTime.fromISO(value.wb_date).toMillis());
        if (index === -1) {
          dataArray.push({
            wb_dateEpoch: DateTime.fromISO(value.wb_date).toMillis(),
            [`wb_value_${key}`]: parseFloat(value.wb_value),
          } as IrrigationDataModel);
        } else {
          dataArray[index][`wb_value_${key}`] = parseFloat(value.wb_value);
        }

        if (parseFloat(value.wb_value) > maxIrrigationChartValue) {
          maxIrrigationChartValue = parseFloat(value.wb_value);
        }
        return dataArray;
      });
    }

    //fill the vegetation's chart array
    waterBalance.fcover_verde && fillArrayChartVegetation('fcover_verde', vegetationDataChart);
    if (waterBalance.fcover_verde && waterBalance.fcover_verde.length) {
      waterBalance.fcover_verde.forEach((data) => {
        if (data && data.wb_date) {
          data.wb_dateEpoch = DateTime.fromISO(data.wb_date).toMillis();
        }
      });
    }
    fillArrayChartVegetation('GDD', vegetationDataChart);
    fillArrayChartVegetation('lai_verde', vegetationDataChart);
    fillArrayChartVegetation('ndvi_planet', vegetationDataChart);

    //fill the irrigation's chart array
    fillArrayChartIrrigation('ETO', irrigationDataChart);
    fillArrayChartIrrigation('ET', irrigationDataChart);
    fillArrayChartIrrigation('irrigation', irrigationDataChart);
    fillArrayChartIrrigation('pluie', irrigationDataChart);
    fillArrayChartIrrigation('SWC1', irrigationDataChart);
    fillArrayChartIrrigation('SWC2', irrigationDataChart);
    fillArrayChartIrrigation('TAW', irrigationDataChart);
    fillArrayChartIrrigation('RAW', irrigationDataChart);
    fillArrayChartIrrigation('DE', irrigationDataChart);
    fillArrayChartIrrigation('GDD_stage', irrigationDataChart);
    fillArrayChartIrrigation('water_excess', irrigationDataChart);
    fillArrayChartIrrigation('RECO', irrigationDataChart);

    // fill the irrigation's Review chart array
    fillArrayChartIrrigation('ETO', irrigationDataReviewChart);
    fillArrayChartIrrigation('ET', irrigationDataReviewChart);
    fillArrayChartIrrigation('irrigation', irrigationDataReviewChart);
    fillArrayChartIrrigation('irrigauto', irrigationDataReviewChart);
    fillArrayChartIrrigation('pluie', irrigationDataReviewChart);
    fillArrayChartIrrigation('SWC1', irrigationDataReviewChart);
    fillArrayChartIrrigation('SWC2', irrigationDataReviewChart);
    fillArrayChartIrrigation('TAW', irrigationDataReviewChart);
    fillArrayChartIrrigation('RAW', irrigationDataReviewChart);
    fillArrayChartIrrigation('DE', irrigationDataReviewChart);
    fillArrayChartIrrigation('DEauto', irrigationDataReviewChart);
    fillArrayChartIrrigation('GDD_stage', irrigationDataReviewChart);
    fillArrayChartIrrigation('water_excess', irrigationDataReviewChart);

    let compareDataChart: {
      [key: number]: VegetationCompareFieldDataModel[];
    } | null = null;
    const selectedField: PixagriField = fieldsData[fieldId];
    const selectedRotation = selectedField.irrigation.find((rotation) => rotation.rotation_id === rotationId);
    if (!selectedRotation) {
      log.error(`rotation ${rotationId} not found in field ${selectedField.field_id}`);
    } else {
      compareDataChart = {
        [selectedRotation.rotation_id]: fillCompareFieldDataChart(
          waterBalance,
          selectedRotation,
          DateTime.fromISO(selectedRotation.campaign_start).get('year'),
        ),
      };
    }

    // Sort the tables per date DESC
    for (const waterBalanceProp in waterBalance) {
      const waterBalancePropArray = waterBalance[waterBalanceProp] as WaterBalanceModel[];
      if (waterBalancePropArray) {
        waterBalancePropArray.sort().reverse();
      }
    }

    let selectedNdviDate: string | null = null;
    let selectedCompareNdviDate: string | null = null;
    if (waterBalance.fcover_verde && waterBalance.fcover_verde.length > 0) {
      selectedNdviDate = waterBalance.fcover_verde[0].wb_date;
      if (waterBalance.fcover_verde.length > 1) {
        selectedCompareNdviDate = waterBalance.fcover_verde[1].wb_date;
      }
    } else if (waterBalance.lai_verde && waterBalance.lai_verde.length > 0) {
      selectedNdviDate = waterBalance.lai_verde[0].wb_date;
      if (waterBalance.lai_verde.length > 1) {
        selectedCompareNdviDate = waterBalance.lai_verde[1].wb_date;
      }
    } else if (waterBalance.ndvi_planet && waterBalance.ndvi_planet.length > 0) {
      selectedNdviDate = waterBalance.ndvi_planet[0].wb_date;
      if (waterBalance.ndvi_planet.length > 1) {
        selectedCompareNdviDate = waterBalance.ndvi_planet[1].wb_date;
      }
    }

    let phenoMainStages: WaterBalancePhenoStage[] = [];
    if (selectedRotation) {
      phenoMainStages.push({
        date: selectedRotation.sowing_date,
        date_epoch: DateTime.fromISO(selectedRotation.sowing_date).toMillis(),
        stage_type: 'stade_s',
        gdd_value: 0,
        isMilestone: true,
        crop_id: selectedRotation.crop_id,
        milestoneIndex: 0,
      });
    }
    if (waterBalance && waterBalance.GDD_stages) {
      const rawPhenoMainStages = waterBalance.GDD_stages.filter((data) => data.isMilestone).map((data) => {
        return {
          date: data.wb_date,
          date_epoch: DateTime.fromISO(data.wb_date).toMillis(),
          stage_type: data.stage_type,
          gdd_value: +data.wb_value,
          isMilestone: data.isMilestone,
          crop_id: data.crop_id,
          milestoneIndex: data.milestoneIndex! + 1,
        } as WaterBalancePhenoStage;
      });
      const sortedRawPhenoMainStages = rawPhenoMainStages.sort((a, b) => a.milestoneIndex! - b.milestoneIndex!);
      phenoMainStages = [...phenoMainStages, ...sortedRawPhenoMainStages];
    }

    const sortedIrrigationDataChart = irrigationDataChart.sort((a, b) => {
      return +a.wb_dateEpoch - +b.wb_dateEpoch;
    });

    const sortedIrrigationDataReviewChart = irrigationDataReviewChart.sort((a, b) => {
      return +a.wb_dateEpoch - +b.wb_dateEpoch;
    });

    const waterBalanceState: WaterBalanceState = {
      summaryIrrigationInfo: summaryIrrigationInfo,
      data: waterBalance,
      selectedNdviDate: selectedNdviDate,
      // Set compare date to selectedNdviDate by default
      selectedCompareNdviDate: selectedCompareNdviDate,
      vegetationDataChart: vegetationDataChart,
      irrigationDataChart: fillCustomDataToIrrigationDataChart(
        sortedIrrigationDataChart,
        maxIrrigationChartValue,
        selectedField,
      ),
      irrigationDataReviewChart: fillCustomDataToIrrigationDataChart(
        sortedIrrigationDataReviewChart,
        maxIrrigationChartValue,
        selectedField,
      ),
      phenoMainStages: phenoMainStages,
      vegetationCompareFieldDataChart: compareDataChart,
      modalWaterIntakeOpen: false,
    };

    yield put(actions.loadWaterBalanceSuccessAction(waterBalanceState));
    if (data.payload.reloadFields) {
      yield fork(reloadFieldsSummaryAfterDelay, 0, fieldId ? [fieldId] : [], true);
    }
    //yield put(fetchSuccess('loadWaterBalance'));
    yield put(removeAlert(alertId));
    yield put(addAlert({id: AutoId.newId('waterbalance'), type: 'success', message: 'bilan hydrique chargé'}));
  } catch (error: any) {
    log.error(error);
    yield put(
      addAlert({
        id: AutoId.newId('waterbalance'),
        type: 'error',
        message: 'Erreur lors du chargement du bilan hydrique',
      }),
    );

    //yield put(fetchError('loadWaterBalance', error.message));
  }
}
function* loadWaterBalanceBackgroundSaga(data: LoadWaterBalanceAction) {
  yield fork(loadWaterBalance, data);
}

function addNewIrrigationRequest(dataToSend: NewIrrigationModel, fieldId: number, fieldRotation: number) {
  const irrigationUrl = `${pixagriApiUrl}/fields/${fieldId}/rotations/${fieldRotation}/irrigate`;
  return api.post(irrigationUrl, dataToSend);
}

function* addNewIrrigation(action: AddIrrigationAction) {
  try {
    yield put(fetchStart('addNewIrrigation'));
    const data = action.payload;
    const dataToSend: NewIrrigationModel = {
      irrigation_date: data.date as string,
      dose_mm: data.dose_mm,
    };
    const fields: {id: number; rotation: number}[] = data.fields;
    //const fieldRotation: number = data.fieldRotation as number;
    const addIrrigationPromise: Promise<void>[] = [];
    fields.forEach((fld) => {
      const req = call(addNewIrrigationRequest, dataToSend, fld.id, fld.rotation);
      addIrrigationPromise.push(req as unknown as Promise<void>);
    });
    yield all(addIrrigationPromise);
    /**
     * reload water on current field consulted only !
     */
    const {selectedFieldId}: FieldsState = yield select((state: AppState) => state.fields);
    const selectedField = fields.find((fld) => fld.id === selectedFieldId);
    if (selectedField) {
      yield put(actions.loadWaterBalance(selectedField.id, selectedField.rotation, true, true));
    }
    const recomputeOtherFieldsPromises: Promise<void>[] = [];
    const othersFields = fields.filter((fld) => fld.id !== selectedFieldId);
    othersFields.forEach((fld) => {
      const req = fork(computeWaterBalanceAndReloadFieldsSummary, fld.id, fld.rotation);
      recomputeOtherFieldsPromises.push(req as unknown as Promise<void>);
    });
    yield all(recomputeOtherFieldsPromises);
    yield put(fetchSuccess('addNewIrrigation'));
  } catch (error: any) {
    yield put(fetchError('addNewIrrigation', error.message));
  }
}

function deleteIrrigationRequest(selectedIrrigation: DeleteDataModel) {
  const {date, fieldId, fieldRotation} = selectedIrrigation;
  const irrigationUrl = `${pixagriApiUrl}/fields/${fieldId}/rotations/${fieldRotation}/irrigate/${date}`;
  return api.delete(irrigationUrl);
}

function* deleteIrrigation(action: DeleteIrrigationAction) {
  try {
    const {fieldId, fieldRotation} = action.payload;

    yield put(fetchStart('deleteIrrigation'));
    yield call(deleteIrrigationRequest, action.payload);
    yield put(fetchSuccess('deleteIrrigation'));
    yield put(actions.loadWaterBalance(fieldId, fieldRotation, true, true));
  } catch (error: any) {
    yield put(fetchError('deleteIrrigation', error.message));
  }
}

function modifyIrrigationRequest(modifiedIrrigation: ModifyDataModel) {
  const {date, dose_mm, fieldId, fieldRotation} = modifiedIrrigation;
  const dataToSend = {
    dose_mm,
    irrigation_date: date,
  };
  const irrigationUrl = `${pixagriApiUrl}/fields/${fieldId}/rotations/${fieldRotation}/irrigate`;
  return api.put(irrigationUrl, dataToSend);
}

function* modifyIrrigation(action: ModifyIrrigationAction) {
  try {
    const {fieldId, fieldRotation} = action.payload;

    yield put(fetchStart('modifyIrrigation'));
    yield call(modifyIrrigationRequest, action.payload);
    yield put(fetchSuccess('modifyIrrigation'));

    yield put(actions.loadWaterBalance(fieldId, fieldRotation, true, true));
  } catch (error: any) {
    yield put(fetchError('modifyIrrigation', error.message));
  }
}

function addNewRainRequest(dataToSend: NewRainModel, fieldId: number, fieldRotation: number) {
  const rainUrl = `${pixagriApiUrl}/fields/${fieldId}/rotations/${fieldRotation}/rain`;
  return api.post(rainUrl, dataToSend);
}

function* addNewRain(action: AddRainAction) {
  try {
    yield put(fetchStart('addNewRain'));
    const data = action.payload;
    const dataToSend: NewRainModel = {
      rain_date: data.date as string,
      dose_mm: data.dose_mm,
    };

    const fields: {id: number; rotation: number}[] = data.fields;
    const addRainPromise: Promise<void>[] = [];
    fields.forEach((fld) => {
      const req = call(addNewRainRequest, dataToSend, fld.id, fld.rotation);
      addRainPromise.push(req as unknown as Promise<void>);
    });
    yield all(addRainPromise);

    const {selectedFieldId}: FieldsState = yield select((state: AppState) => state.fields);
    const selectedField = fields.find((fld) => fld.id === selectedFieldId);
    if (selectedField) {
      yield put(actions.loadWaterBalance(selectedField.id, selectedField.rotation, true, true));
    }
    const recomputeOtherFieldsPromises: Promise<void>[] = [];
    const othersFields = fields.filter((fld) => fld.id !== selectedFieldId);
    othersFields.forEach((fld) => {
      const req = fork(computeWaterBalanceAndReloadFieldsSummary, fld.id, fld.rotation);
      recomputeOtherFieldsPromises.push(req as unknown as Promise<void>);
    });
    yield all(recomputeOtherFieldsPromises);
    yield put(fetchSuccess('addNewRain'));
  } catch (error: any) {
    yield put(fetchError('addNewRain', error.message));
  }
}

function deleteRainRequest(selectedRain: DeleteDataModel) {
  const {date, fieldId, fieldRotation} = selectedRain;
  const rainUrl = `${pixagriApiUrl}/fields/${fieldId}/rotations/${fieldRotation}/rain/${date}`;
  return api.delete(rainUrl);
}

function* deleteRain(action: DeleteRainAction) {
  try {
    const {fieldId, fieldRotation} = action.payload;

    yield put(fetchStart('deleteRain'));
    yield call(deleteRainRequest, action.payload);
    yield put(fetchSuccess('deleteRain'));

    yield put(actions.loadWaterBalance(fieldId, fieldRotation, true, true));
  } catch (error: any) {
    yield put(fetchError('deleteRain', error.message));
  }
}

function modifyRainRequest(modifiedRain: ModifyDataModel) {
  const {date, dose_mm, fieldId, fieldRotation} = modifiedRain;
  const dataToSend = {
    dose_mm,
    rain_date: date,
  };
  const rainUrl = `${pixagriApiUrl}/fields/${fieldId}/rotations/${fieldRotation}/rain`;
  return api.put(rainUrl, dataToSend);
}

function* modifyRain(action: ModifyRainAction) {
  try {
    const {fieldId, fieldRotation} = action.payload;

    yield put(fetchStart('modifyRain'));
    yield call(modifyRainRequest, action.payload);
    yield put(fetchSuccess('modifyRain'));

    yield put(actions.loadWaterBalance(fieldId, fieldRotation, true, true));
  } catch (error: any) {
    yield put(fetchError('modifyRain', error.message));
  }
}

function getPdfRequest(selectedCampaign: number) {
  const getpdfUrl = `${pixagriApiUrl}/campaigns/${selectedCampaign}/pdf`;
  return api.post(getpdfUrl);
}
function* getPdfWaterBalanceSaga() {
  try {
    yield put(fetchStart('getPdfWaterBalanceSaga'));
    const {selectedCampaign}: FieldsState = yield select((state: AppState) => state.fields);
    if (selectedCampaign) {
      yield call(getPdfRequest, selectedCampaign);

      yield put(actions.updatePdfWaterBalanceStatus('success'));
      yield put(fetchSuccess('getPdfWaterBalanceSaga'));
    } else {
      throw new Error(`Cannot get pdf, invalid campaign => ${selectedCampaign}`);
    }
  } catch (error: any) {
    yield put(fetchError('getPdfWaterBalanceSaga', error.message));
    yield put(actions.updatePdfWaterBalanceStatus('error'));
  }
}

function modifyPhenoStageDateRequest(dataToSend: WaterBalancePhenoStage[], fieldId: number, rotationId: number) {
  const rainUrl = `${pixagriApiUrl}/fields/${fieldId}/rotations/${rotationId}/stages`;
  return api.post(rainUrl, dataToSend);
}

interface modifyPhenoStageDateResponse {
  data: {
    stages: WaterBalancePhenoStage[];
    stagesUpdated: WaterBalancePhenoStage[];
    status: string;
    updateCount: number;
  };
}

function* modifyPhenoStageDate(action: ModifyPhenoStageDateAction) {
  try {
    yield put(fetchStart('modifyPhenoStageDate'));
    const {modifiedPhenoStageDate, fieldId, rotationId} = action.payload;
    const response: modifyPhenoStageDateResponse = yield call(
      modifyPhenoStageDateRequest,
      modifiedPhenoStageDate,
      fieldId,
      rotationId,
    );
    const {phenoMainStages}: WaterBalanceState = yield select((state: AppState) => state.waterBalance);
    const typedPhenoMainStages: WaterBalancePhenoStage[] = phenoMainStages;

    const newPhenoStages = typedPhenoMainStages.map((stage) => {
      const stageUpdated = response.data.stagesUpdated.find((s) => s.stage_type === stage.stage_type);
      if (stageUpdated) {
        return {
          ...stageUpdated,
          date_epoch: DateTime.fromISO(stageUpdated.date).toMillis(),
        };
      } else {
        return stage;
      }
    });

    yield put(actions.modifyPhenoStageDateSuccess(newPhenoStages));
    yield put(fetchSuccess('modifyPhenoStageDate'));
  } catch (error: any) {
    yield put(fetchError('modifyPhenoStageDate', error.message));
  }
}

function* modifyWaterIntake(action: ModifyWaterIntakeAction) {
  try {
    yield put(fetchStart('modifiedWaterIntakeSaga'));
    const {modifiedWaterIntake, selectedData} = action.payload;
    const originalWaterIntakes = selectedData.filter(
      (data: WaterBalanceModel) => data.wb_date === modifiedWaterIntake.date,
    );

    if (originalWaterIntakes.length === 1 && originalWaterIntakes[0].wb_type !== modifiedWaterIntake.wb_type) {
      const deletedData: DeleteDataModel = {
        date: modifiedWaterIntake.date as string,
        fieldId: modifiedWaterIntake.fieldId,
        fieldRotation: modifiedWaterIntake.fieldRotation,
      };
      const addData: NewDataModel = {
        dose_mm: modifiedWaterIntake.dose_mm.toString(),
        date: modifiedWaterIntake.date as string,
        type: modifiedWaterIntake.wb_type,
        fields: [
          {
            id: modifiedWaterIntake.fieldId,
            rotation: modifiedWaterIntake.fieldRotation,
          },
        ],
      };

      switch (modifiedWaterIntake.wb_type) {
        case IrrigationDataPicked.IRRIGATION:
          yield put(actions.deleteRain(deletedData));
          yield put(actions.addIrrigation(addData));
          break;
        case IrrigationDataPicked.PLUIE:
          yield put(actions.deleteIrrigation(deletedData));
          yield put(actions.addRain(addData));
          break;
        default:
          throw new Error(`Unknown wb_type: ${modifiedWaterIntake.wb_type}`);
      }
    } else {
      switch (modifiedWaterIntake.wb_type) {
        case IrrigationDataPicked.IRRIGATION:
          yield put(actions.modifyIrrigation(modifiedWaterIntake));
          break;
        case IrrigationDataPicked.PLUIE:
          yield put(actions.modifyRain(modifiedWaterIntake));
          break;
        default:
          throw new Error(`Unknown wb_type: ${modifiedWaterIntake.wb_type}`);
      }
    }

    yield put(fetchSuccess('modifiedWaterIntakeSaga'));
  } catch (error: any) {
    yield put(fetchError('modifiedWaterIntakeSaga', error.message));
  }
}

export function* waterBalanceSagas() {
  yield all([
    takeEvery(LOAD_WATER_BALANCE, loadWaterBalanceBackgroundSaga),
    takeLatest(ADD_IRRIGATION, addNewIrrigation),
    takeLatest(DELETE_IRRIGATION, deleteIrrigation),
    takeLatest(MODIFY_IRRIGATION, modifyIrrigation),
    takeLatest(ADD_RAIN, addNewRain),
    takeLatest(DELETE_RAIN, deleteRain),
    takeLatest(MODIFY_RAIN, modifyRain),
    takeEvery(COMPARE_FIELDS, loadFieldsWaterBalance),
    takeLatest(GET_PDF_WATERBALANCE, getPdfWaterBalanceSaga),
    takeEvery(MODIFY_PHENO_STAGE_DATE, modifyPhenoStageDate),
    takeLatest(MODIFY_WATER_INTAKE, modifyWaterIntake),
  ]);
}
/**
 * Add to each item of irrigationDataChart range numbers when DE is over TAW or RAW (use to draw area in chart)
 * ex: treshold_raw : [value of RAW, value of DE] as number[]
 * Fill stage divider if GDD_stage exist in waterbalance response
 * Define Prediction area base on current date
 * @param irrigationDataChart
 * @returns
 */
function fillCustomDataToIrrigationDataChart(
  irrigationDataChart: IrrigationDataModel[],
  maxIrrigationChartValue: number,
  selectedField: PixagriField,
) {
  const dateNowMillis = DateTime.now().toMillis();

  irrigationDataChart.forEach((data: IrrigationDataModel) => {
    /**
     * Look if is prediction area
     */
    const date = typeof data.wb_dateEpoch === 'number' ? data.wb_dateEpoch : parseFloat(data.wb_dateEpoch);

    if (date >= dateNowMillis) {
      data.prediction = [0, maxIrrigationChartValue];
    }

    /**
     * Add a stage divider if necessary
     */
    if (data.wb_value_GDD_stage) {
      data.stage_divider = maxIrrigationChartValue;
    }

    /**
     * Build warning area if DE > TAW or RAW
     */
    if (data.wb_value_DE && data.wb_value_RAW && data.wb_value_DE >= data.wb_value_RAW) {
      data.treshold_raw = [data.wb_value_RAW, data.wb_value_DE] as number[];
    } else {
      // use to extend aera until intersection
      data.treshold_raw = [data.wb_value_DE, data.wb_value_DE] as number[];
    }

    if (data.wb_value_DE && data.wb_value_TAW && data.wb_value_DE >= data.wb_value_TAW) {
      data.treshold_taw = [data.wb_value_TAW, data.wb_value_DE] as number[];
    } else {
      // use to extend aera until intersection
      data.treshold_taw = [data.wb_value_DE, data.wb_value_DE] as number[];
    }

    if (data.wb_value_RAW && selectedField?.field_thresholds) {
      const thresholds = selectedField.field_thresholds;
      const wb_value_RAW_medium = data.wb_value_RAW * (thresholds.medium || 0);
      const wb_value_RAW_low = data.wb_value_RAW * (thresholds.low || 0);

      data.wb_value_RAW_medium = wb_value_RAW_medium;
      data.wb_value_RAW_low = wb_value_RAW_low;
      data.treshold_RAW_low = [wb_value_RAW_low, 0];
      data.treshold_RAW_medium = [wb_value_RAW_medium, wb_value_RAW_low];
      data.treshold_RAW_high = [data.wb_value_RAW, wb_value_RAW_medium];
    }
  });
  return irrigationDataChart;
}
